import axios from "axios";

// The ONE authoritative backend URL for this whole app — every module
// under src/api|services imports this client, nothing calls axios/fetch
// directly on its own, and nothing hardcodes localhost:8000/8001 here.
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8002";

export const TOKEN_KEY = "pos_user_token";
export const USER_KEY = "pos_user_data";
export const UNAUTHORIZED_EVENT = "pos-user-unauthorized";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
});

// Attach the bearer token (if any) to every request — centralized here so
// no individual api/service module has to remember to do it.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Centralized error handling. A 401 always means "this session is no
// longer valid" — clear it and let AuthContext's own state drive any
// redirect; this interceptor never navigates directly.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
      }
      // 403/404/422/500 are left to the caller to handle with context
      // (e.g. a 404 on a product detail page means "not found", not a
      // global error) — only 401 has one universal, safe reaction.
      return Promise.reject(error);
    }
    if (error.code === "ECONNABORTED") {
      return Promise.reject(new Error("The request timed out. Please try again."));
    }
    return Promise.reject(new Error("Unable to connect to the server."));
  }
);

// Turns any error (axios error, plain Error, or a raw string) into a safe,
// user-facing message — FastAPI's own 422 sends `detail` as an array of
// validation objects, not a string, so this normalizes both shapes.
export function friendlyError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
  if (err?.message) return err.message;
  return fallback;
}

export { API_BASE_URL };
export default api;
